import { useState } from 'react';
import { Button, PickUpUser, toast } from '@cencosud-cencommerce/paris-uikit';
import { type DocumentType } from '@cencosud-cencommerce/paris-uikit';

import {
  isDniFeatureEnabled,
  isDniRegisteredFeatureEnabled
} from '@/config/featureFlags';
import {
  type IdentificationDocumentType,
  isDni,
  isRut
} from '@/modules/shared/types/IdentificationDocumentType';
import { sendEvent } from '@/modules/shared/utils/gtmEvents/sendEvents/sendBuyNowEvents';
import { useSetOnBehalfBuyNow } from '@/Pages/BuyNowPage/hooks/useSetOnBehalfBuyNow';
import {
  EVENT_TAG_PICKUP_ANOTHER_PERSON,
  LABEL_PICKUP_ANOTHER_PERSON,
  TOAST_DELETE_ERROR
} from '@/Pages/BuyNowPage/PaymentAndShipping/partials/PickupOptions/PickupPerson/constants';
import type { PickupPersonFormValues } from '@/Pages/BuyNowPage/PaymentAndShipping/partials/PickupOptions/PickupPerson/interfaces';
import { PickupPersonModal } from '@/Pages/BuyNowPage/PaymentAndShipping/partials/PickupOptions/PickupPerson/PickupPersonModal';
import { useCustomerContext } from '@/sections/customer/CustomerContext';

interface PickupPersonProps {
  deliveryGroupId: string;
}

const isValidDocumentType = (type: unknown): type is DocumentType =>
  isRut(type as IdentificationDocumentType) ||
  isDni(type as IdentificationDocumentType);

export const PickupPerson = ({ deliveryGroupId }: PickupPersonProps) => {
  const { trigger: setOnBehalf } = useSetOnBehalfBuyNow();
  const {
    customer: { data: customer }
  } = useCustomerContext();

  const [pickupId, setPickupId] = useState<PickupPersonFormValues | undefined>(
    undefined
  );

  const [isPickupModalOpen, setIsPickupModalOpen] = useState(false);

  const isRegistered = customer !== null;
  const isDniEnabled = isRegistered
    ? isDniRegisteredFeatureEnabled()
    : isDniFeatureEnabled();

  const handleButtonClick = () => {
    sendEvent(EVENT_TAG_PICKUP_ANOTHER_PERSON);
    setIsPickupModalOpen(true);
  };

  const handleDeletePickupPerson = async () => {
    try {
      await setOnBehalf({
        deliveryGroupId,
        authorizedPerson: {}
      });

      setPickupId(undefined);
    } catch (error) {
      toast(TOAST_DELETE_ERROR);
    }
  };

  return (
    <>
      <section className="co-px-3 co-py-4 co-border-t co-border-t-neutral-300">
        {!pickupId && (
          <Button
            variant="secondary"
            iconLeft="person"
            onClick={handleButtonClick}
            block
          >
            {LABEL_PICKUP_ANOTHER_PERSON}
          </Button>
        )}

        {pickupId && (
          <PickUpUser
            fullName={pickupId.completeName}
            {...(isDniEnabled
              ? isValidDocumentType(pickupId.documentType)
                ? {
                    documentID: pickupId.documentID || pickupId.pickupPersonRut,
                    documentType: pickupId.documentType
                  }
                : {
                    documentID: pickupId.documentID || pickupId.pickupPersonRut,
                    documentType: undefined
                  }
              : {
                  rut: pickupId.pickupPersonRut
                })}
            onClickDeleteButton={handleDeletePickupPerson}
            onClickEditButton={() => setIsPickupModalOpen(true)}
          />
        )}
      </section>

      <PickupPersonModal
        isPickupModalOpen={isPickupModalOpen}
        setIsPickupModalOpen={setIsPickupModalOpen}
        setPickupId={setPickupId}
        deliveryGroupId={deliveryGroupId}
      />
    </>
  );
};

PickupPerson.displayName = 'PickupPerson';

export default PickupPerson;
